/**
 * The dashboard's KPI tile: icon, figure, optional completion ring, and a
 * foot line carrying a sparkline, a trend delta or a short note.
 *
 * Extracted from JobsListScreen so the reports queue reads as the same
 * product. Trend and spark are optional — a screen with no time series (the
 * reports queue) simply omits them and the foot collapses.
 */

import { Icon, type IconName } from './Icon.js';
import { trendDelta, type Trend } from './trend.js';

export function Kpi({
  label,
  value,
  unit,
  tone,
  icon,
  percent,
  percentCaption,
  trend,
  spark,
  footnote,
  formatValue,
  onClick,
}: {
  label: string;
  value: number | undefined;
  /** Omitted where the figure needs no qualifier — a currency total, say. */
  unit?: string;
  /** `neutral` is for counts that carry no judgement — cancelled, expected. */
  tone: 'blue' | 'green' | 'amber' | 'red' | 'violet' | 'neutral';
  icon: IconName;
  percent?: number | undefined;
  percentCaption?: string;
  trend?: Trend | undefined;
  spark?: Trend | undefined;
  footnote?: { text: string; tone: 'warn' | 'risk' | 'good' } | undefined;
  /** Render the figure some other way — currency, duration. Defaults to a
      grouped integer. */
  formatValue?: (value: number) => string;
  onClick?: () => void;
}) {
  const delta = trendDelta(trend);
  const Tag = onClick ? 'button' : 'div';
  const sparkPoints = spark?.sufficient
    ? spark.points
        .map((point, index, all) => {
          const max = Math.max(1, ...all.map((item) => item.count));
          const x = (index / Math.max(1, all.length - 1)) * 200;
          return `${x.toFixed(1)},${(26 - (point.count / max) * 22).toFixed(1)}`;
        })
        .join(' ')
    : undefined;
  return (
    <Tag className={`kpi kpi--${tone}`} {...(onClick ? { type: 'button' as const, onClick } : {})}>
      <div className="kpi__top">
        <i className="kpi__icon">
          <Icon name={icon} />
        </i>
        <div className="kpi__figures">
          <span className="kpi__label">{label}</span>
          <p className="kpi__value">
            <strong>
              {value === undefined
                ? '—'
                : (formatValue ?? ((n: number) => n.toLocaleString()))(value)}
            </strong>
            {unit && <small>{unit}</small>}
          </p>
        </div>
        {percent !== undefined && (
          <div className="kpi__ring">
            <Ring percent={percent} />
            {percentCaption && <small>{percentCaption}</small>}
          </div>
        )}
      </div>
      <div className="kpi__foot">
        {sparkPoints && (
          <svg
            className="kpi__spark"
            viewBox="0 0 200 30"
            preserveAspectRatio="none"
            role="img"
            aria-label={`${label} daily trend`}
          >
            <polyline
              points={sparkPoints}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
        {delta && (
          <span className={`trend-delta ${delta.up ? 'trend-delta--up' : 'trend-delta--down'}`}>
            <Icon name={delta.up ? 'up' : 'down'} />
            {delta.percent}%<small>vs prior 15 days</small>
          </span>
        )}
        {!delta && trend && <span className="trend-delta trend-delta--none">No trend history</span>}
        {footnote && (
          <span className={`kpi__note kpi__note--${footnote.tone}`}>
            {footnote.tone === 'warn' && <Icon name="alert" />}
            {footnote.text}
          </span>
        )}
      </div>
    </Tag>
  );
}

/** Donut gauge used by the KPI cards. */
function Ring({ percent }: { percent: number }) {
  const radius = 21;
  const circumference = 2 * Math.PI * radius;
  const filled = (percent / 100) * circumference;
  return (
    <svg viewBox="0 0 52 52" role="img" aria-label={`${percent}%`}>
      <circle cx="26" cy="26" r={radius} fill="none" stroke="#e6ecf4" strokeWidth="6" />
      <circle
        cx="26"
        cy="26"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference - filled}`}
        transform="rotate(-90 26 26)"
      />
      {/* 100% needs four glyphs in the same well as two — inline style so it
          beats the class rule. */}
      <text
        x="26"
        y="26"
        className="ring__text"
        style={{ fontSize: percent >= 100 ? '10.5px' : '13px' }}
      >
        {percent}%
      </text>
    </svg>
  );
}
